/**
 * Geo primitives shared by both apps. Coordinate order is GeoJSON `[lng, lat]`
 * everywhere — this matches MapLibre/terra-draw output AND the ClickHouse
 * `location_geo_coordinates` Point (`.1` = lng, `.2` = lat), so no per-app
 * conversion is needed.
 *
 * Implementations are backed by Turf.js (`@turf/*`) so the client pre-check and
 * the server enforcement run identical, battle-tested geometry. `GeoPolygon` is a
 * GeoJSON Polygon geometry, which Turf functions accept directly; we only wrap
 * with the `@turf/helpers` constructors where Turf requires a Feature/collection.
 */
import area from '@turf/area';
import bbox from '@turf/bbox';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import difference from '@turf/difference';
import distance from '@turf/distance';
import kinks from '@turf/kinks';
import { union } from '@turf/union';
import { featureCollection, polygon as turfPolygon } from '@turf/helpers';
/**
 * Bounding box of a polygon's outer ring. Pure + isomorphic so it can run in the
 * backend SQL builder and in the frontend (e.g. to fit the map to a project's
 * area). Backed by `@turf/bbox`.
 */
export function bboxOfPolygon(poly) {
    const ring = poly.coordinates[0] ?? [];
    if (ring.length === 0) {
        throw new Error('bboxOfPolygon: polygon has no outer ring');
    }
    const [minLng, minLat, maxLng, maxLat] = bbox(poly);
    return { minLng, minLat, maxLng, maxLat };
}
/**
 * Geodesic area of a polygon in **km²**, via `@turf/area` (spherical-excess,
 * JPL Chamberlain–Duquette — the same algorithm and Earth radius the previous
 * hand-rolled helper used, so values are unchanged) divided to km². Holes are
 * subtracted; a degenerate ring (< 3 distinct points) returns 0.
 */
export function polygonAreaKmSq(poly) {
    const ring = poly.coordinates[0] ?? [];
    if (ring.length < 4)
        return 0; // need a closed ring of 3+ distinct vertices
    return area(poly) / 1e6;
}
/**
 * Point-in-polygon test (`@turf/boolean-point-in-polygon`). `pt` is `[lng, lat]`.
 * Hole-aware, and consistent with the ClickHouse `pointInPolygon` semantics so
 * the frontend pre-check and the backend enforcement agree.
 */
export function pointInPolygon(pt, poly) {
    const ring = poly.coordinates[0] ?? [];
    if (ring.length < 4)
        return false;
    return booleanPointInPolygon(pt, poly);
}
/**
 * True iff `inner` is geometrically inside `outer`. Computed as
 * `area(inner − outer) ≈ 0` via `@turf/difference`: if no part of `inner` falls
 * outside `outer`, the difference is empty. Unlike a vertex-only check (the
 * previous approximation, and what Turf's `booleanWithin`/`booleanContains` do
 * for polygon-in-polygon), this catches the case where every vertex of `inner`
 * sits inside `outer` but an edge bulges out — so the org-boundary invariant is
 * actually enforced. A tiny relative residual is tolerated to absorb clipping
 * round-off along shared edges.
 */
export function polygonInsidePolygon(inner, outer) {
    const innerRing = inner.coordinates[0] ?? [];
    const outerRing = outer.coordinates[0] ?? [];
    if (innerRing.length < 4 || outerRing.length < 4)
        return false;
    const remainder = difference(featureCollection([turfPolygon(inner.coordinates), turfPolygon(outer.coordinates)]));
    if (!remainder)
        return true; // nothing of inner lies outside outer
    const innerArea = area(inner);
    if (innerArea === 0)
        return true;
    return area(remainder) / innerArea < 1e-6;
}
/** Geodesic great-circle distance between two `[lng, lat]` points, in km. */
export function distanceKm(a, b) {
    return distance(a, b, { units: 'kilometers' });
}
/**
 * Structural + topological validity of a polygon: a closed outer ring of at
 * least 3 distinct vertices that does not self-intersect (`@turf/kinks` finds
 * no crossing points). Rejects "bowtie" polygons that area/containment math
 * can't reason about meaningfully.
 */
export function isValidPolygon(poly) {
    const ring = poly.coordinates[0] ?? [];
    if (ring.length < 4)
        return false;
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (!first || !last || first[0] !== last[0] || first[1] !== last[1])
        return false;
    return kinks(poly).features.length === 0;
}
/**
 * Area of the **union** of several polygons in km² — overlapping coverage is
 * counted once (unlike summing each polygon's area). Backed by `@turf/union` +
 * `@turf/area`. An empty input is 0.
 */
export function unionAreaKmSq(polys) {
    const valid = polys.filter((p) => (p.coordinates[0]?.length ?? 0) >= 4);
    if (valid.length === 0)
        return 0;
    if (valid.length === 1)
        return polygonAreaKmSq(valid[0]);
    const merged = union(featureCollection(valid.map((p) => turfPolygon(p.coordinates))));
    return merged ? area(merged) / 1e6 : 0;
}
//# sourceMappingURL=geo.js.map